import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Code } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupCodesProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const categoryOptions = [
  { value: 'service-type', label: 'Service Type' },
  { value: 'document-type', label: 'Document Type' },
  { value: 'court-type', label: 'Court Type' },
  { value: 'status-code', label: 'Status Code' },
  { value: 'billing-code', label: 'Billing Code' },
  { value: 'workflow-trigger', label: 'Workflow Trigger' },
];

const statusOptions = [
  { value: 'executed', label: 'Executed' },
  { value: 'received', label: 'Received' },
  { value: 'inactive', label: 'Inactive' },
];

const serviceFlagLabels: Record<string, string> = {
  filing: 'Filing',
  research: 'Research',
  serve: 'Serve',
  courtesyCopy: 'Courtesy Copy',
  countyRecorder: 'County Recorder',
  skipTrace: 'Skip Trace',
  writService: 'Writ Service',
  stateFiling: 'State Filing',
  eService: 'E-Service',
  vendor: 'Vendor',
};

const SetupCodes: React.FC<SetupCodesProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [codes, setCodes] = useState<Code[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedCode, setSelectedCode] = useState<Code | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Code>>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchCodes = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      const data = await get<{ data: Code[] }>(`/api/admin/setup/codes?${params}`);
      setCodes(data.data || []);
    } catch (error) {
      console.error('Failed to fetch codes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter, categoryFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchCodes();
  }, [fetchCodes]);

  const handleRowClick = (code: Code) => {
    setSelectedCode(code);
    setFormData(code);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedCode(null);
    setFormData({
      code: '',
      name: '',
      category: 'service-type',
      status: 'inactive',
      filing: false,
      research: false,
      serve: false,
      courtesyCopy: false,
      countyRecorder: false,
      skipTrace: false,
      writService: false,
      stateFiling: false,
      eService: false,
      vendor: false,
      autoApply: false,
      requiresApproval: false,
      notifyClient: false,
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/codes', formData);
      } else {
        await patch(`/api/admin/setup/codes/${selectedCode?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchCodes();
    } catch (error) {
      console.error('Failed to save code:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search codes..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        categoryFilter={categoryFilter}
        categoryOptions={categoryOptions}
        onCategoryChange={setCategoryFilter}
        onAdd={handleAdd}
        addLabel="Add Code"
      />

      {/* Custom grid-style table for codes with boolean matrix */}
      <div
        className="bg-white rounded-lg border overflow-hidden"
        style={{ borderColor: brand.colors.border }}
      >
        {isLoading ? (
          <div className="p-8 text-center">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto"
              style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
            />
          </div>
        ) : codes.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              No codes found
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: brand.colors.bgLight }}>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Code</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Category</th>
                  {Object.keys(serviceFlagLabels).map(flag => (
                    <th
                      key={flag}
                      className="px-1 py-2 text-center text-xs font-medium uppercase"
                      style={{ color: brand.colors.textLight }}
                      title={serviceFlagLabels[flag]}
                    >
                      {serviceFlagLabels[flag].substring(0, 3)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase" style={{ color: brand.colors.textLight }}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: brand.colors.border }}>
                {codes.map(code => (
                  <tr
                    key={code.id}
                    onClick={() => handleRowClick(code)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2 font-medium" style={{ color: brand.colors.link }}>
                      <div className="flex items-center gap-2">
                        <span>{code.code}</span>
                        <SetupItemPreviewPopover
                          brand={brand}
                          title={code.name}
                          fields={[
                            { label: 'Code', value: code.code },
                            { label: 'Category', value: code.category.replace('-', ' ') },
                            { label: 'Description', value: code.description || '-' },
                            { label: 'Auto Apply', value: code.autoApply ? 'Yes' : 'No' },
                            { label: 'Approval', value: code.requiresApproval ? 'Required' : 'Not required' },
                          ]}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2" style={{ color: brand.colors.text }}>
                      {code.name}
                    </td>
                    <td className="px-3 py-2 capitalize" style={{ color: brand.colors.textLight }}>
                      {code.category.replace('-', ' ')}
                    </td>
                    {Object.keys(serviceFlagLabels).map(flag => (
                      <td key={flag} className="px-1 py-2 text-center">
                        {(code as Record<string, unknown>)[flag] ? (
                          <div
                            className="w-4 h-4 rounded-full mx-auto"
                            style={{ backgroundColor: brand.colors.primary }}
                          />
                        ) : (
                          <div
                            className="w-4 h-4 rounded-full border mx-auto"
                            style={{ borderColor: brand.colors.border }}
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <StatusBadge status={code.status} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Code' : 'Edit Code'}
        subtitle={selectedCode?.code}
        brand={brand}
        Icons={Icons}
        width="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Code"
              name="code"
              value={formData.code || ''}
              onChange={handleInputChange}
              required
              placeholder="e.g., SRV-001"
            />
            <FormSelect
              brand={brand}
              label="Category"
              name="category"
              value={formData.category || 'service-type'}
              onChange={handleInputChange}
              options={categoryOptions}
            />
          </div>

          <FormInput
            brand={brand}
            label="Name"
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            required
          />

          <FormTextarea
            brand={brand}
            label="Description"
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            rows={2}
          />

          <FormSelect
            brand={brand}
            label="Status"
            name="status"
            value={formData.status || 'inactive'}
            onChange={handleInputChange}
            options={statusOptions}
          />

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Service Trigger Flags
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(serviceFlagLabels).map(flag => (
                <FormCheckbox
                  key={flag}
                  brand={brand}
                  label={serviceFlagLabels[flag]}
                  name={flag}
                  checked={(formData as Record<string, unknown>)[flag] as boolean || false}
                  onChange={handleInputChange}
                />
              ))}
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Workflow Settings
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <FormCheckbox
                brand={brand}
                label="Auto Apply"
                name="autoApply"
                checked={formData.autoApply || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Requires Approval"
                name="requiresApproval"
                checked={formData.requiresApproval || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Notify Client"
                name="notifyClient"
                checked={formData.notifyClient || false}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Fee Adjustment (Optional)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Additional Fee ($)"
                name="additionalFee"
                type="number"
                value={formData.additionalFee || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Fee Description"
                name="feeDescription"
                value={formData.feeDescription || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <FormTextarea
              brand={brand}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupCodes;
